import { autoPublishChannels, channelsFromSchedule } from '../shared/autoPublish';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../shared/components/Button';
import { Field, Input, Textarea } from '../shared/components/Field';
import { Alert, EmptyState, Spinner } from '../shared/components/feedback';
import { Avatar } from '../shared/components/Avatar';
import { Switch } from '../shared/components/Switch';
import { ToneProfileView } from './tone/ToneProfileView';
import { useActiveAccount } from '../account/ActiveAccountContext';
import { useDialog } from '../shared/dialog/DialogProvider';
import * as threadProfileApi from '../api/threadProfile';
import * as threadScheduleApi from '../api/threadSchedule';
import * as threadReferenceSearchApi from '../api/threadReferenceSearch';
import * as threadAccountApi from '../api/threadAccount';
import { ApiError } from '../api/client';
import type {
  ThreadAccountSchedule,
  ThreadToneProfileResponse,
} from '../api/types';
import type {
  ReferencePostStyle,
  ThreadReferencePost,
} from '../api/threadReferenceSearch';
import { REFERENCE_POST_STYLES } from '../api/threadReferenceSearch';
import { formatWallClockKo } from '../shared/datetimeWallClock';

const TONE_EXAMPLE_HINTS = [
  '오늘 퇴근길에 느낀 짧은 생각…',
  '개발하면서 자주 하는 실수 정리…',
  '요즘 읽은 책 한 줄 요약 + 내 생각…',
];

const MIX_COUNTS = [0, 1, 2, 3, 4, 5] as const;
const IMAGE_PER_POST = [1, 2, 3, 5, 10] as const;

function splitDailyMix(
  dailyLimit: number,
  persona?: number | null,
  product?: number | null,
  hasPersona?: boolean
) {
  const limit = Math.min(5, Math.max(1, dailyLimit || 5));
  if (persona != null && product != null) {
    const p = Math.max(0, Math.min(limit, persona));
    return { persona: p, product: Math.max(0, limit - p) };
  }
  if (hasPersona) {
    const p = Math.ceil(limit / 2);
    return { persona: p, product: limit - p };
  }
  return { persona: 0, product: limit };
}

function imageCountsFromSchedule(
  data: ThreadAccountSchedule,
  mix: { persona: number; product: number }
) {
  return {
    personaImages: data.personaImagePostCount ?? (data.includeImage ? mix.persona : 0),
    productImages: data.productImagePostCount ?? mix.product,
    perPost: Math.min(10, Math.max(1, data.productImagesPerPost ?? 1)),
  };
}

export default function AccountPage() {
  const { selectedAccountId } = useActiveAccount();
  return <AccountSettings key={selectedAccountId ?? 'none'} />;
}

function AccountSettings() {
  const {
    accounts,
    loading,
    error,
    selectedAccount,
    selectedAccountId,
    selectAccount,
    fetchAccounts,
  } = useActiveAccount();
  const { alert, confirm } = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleting, setDeleting] = useState(false);

  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{
    tone: 'success' | 'danger';
    text: string;
  } | null>(null);

  const [schedule, setSchedule] = useState<ThreadAccountSchedule | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleRefresh, setScheduleRefresh] = useState(0);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [channels, setChannels] = useState(channelsFromSchedule());
  const enabled = Object.values(channels).some(Boolean);
  const [reviewRequired, setReviewRequired] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState('180');
  const [dailyLimit, setDailyLimit] = useState('5');
  const [personaDailyCount, setPersonaDailyCount] = useState(2);
  const [productDailyCount, setProductDailyCount] = useState(3);
  const [personaImagePostCount, setPersonaImagePostCount] = useState(0);
  const [productImagePostCount, setProductImagePostCount] = useState(3);
  const [productImagesPerPost, setProductImagesPerPost] = useState(1);
  const [commentPersona, setCommentPersona] = useState('');
  const [toneEnabled, setToneEnabled] = useState(true);
  const [referenceCopyEnabled, setReferenceCopyEnabled] = useState(false);
  const [referencePost, setReferencePost] = useState('');
  const [referenceKeyword, setReferenceKeyword] = useState('');
  const [referenceStyle, setReferenceStyle] = useState<ReferencePostStyle>('ALL');
  const [referenceResults, setReferenceResults] = useState<ThreadReferencePost[]>([]);
  const [referenceSearching, setReferenceSearching] = useState(false);
  const [referenceSearchError, setReferenceSearchError] = useState<string | null>(null);
  const [referenceSearchErrorTone, setReferenceSearchErrorTone] = useState<
    'danger' | 'warning'
  >('danger');
  const [expandedKeywords, setExpandedKeywords] = useState<string[]>([]);

  const [toneEssay, setToneEssay] = useState('');
  const [toneSaving, setToneSaving] = useState(false);
  const [toneToggleSaving, setToneToggleSaving] = useState(false);
  const [toneError, setToneError] = useState<string | null>(null);
  const [tone, setTone] = useState<ThreadToneProfileResponse | null>(null);
  const [accountTab, setAccountTab] = useState<'publish' | 'tone'>('publish');

  const profile = selectedAccount?.threadAccountProfileResponse;
  const handledCallback = useRef(false);

  const displayName = profile?.threadUserName
    ? `@${profile.threadUserName}`
    : selectedAccount
      ? `계정 #${selectedAccount.id}`
      : '';

  useEffect(() => {
    if (handledCallback.current) return;
    const connected = searchParams.get('connected');
    const oauthError = searchParams.get('error');
    if (!connected && !oauthError) return;
    handledCallback.current = true;

    // 예전 팝업 연동 방식 대비: opener가 있으면 메인 창에 알리고 팝업 닫기
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          {
            type: 'threads-oauth-result',
            connected,
            error: oauthError,
          },
          window.location.origin
        );
        window.close();
        return;
      } catch {
        // opener 통신 실패 시 현재 창에서 계속 처리
      }
    }

    if (connected) {
      const id = Number(connected);
      if (Number.isFinite(id) && id > 0) {
        fetchAccounts()
          .then(() => {
            selectAccount(id);
            setMessage({
              tone: 'success',
              text: 'Threads 계정이 연동되었습니다.',
            });
          })
          .catch(() => undefined);
      }
    } else if (oauthError) {
      setMessage({
        tone: 'danger',
        text: 'Threads 계정 연동에 실패했습니다. 다시 시도해 주세요.',
      });
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, fetchAccounts, selectAccount]);

  useEffect(() => {
    setDescription(profile?.description ?? '');
    setTone(profile?.threadToneProfileResponse ?? null);
  }, [profile]);

  useEffect(() => {
    if (selectedAccountId == null) {
      setSchedule(null);
      return;
    }
    let cancelled = false;
    setScheduleLoading(true);
    setScheduleError(null);
    threadScheduleApi
      .getSchedule(selectedAccountId)
      .then((data) => {
        if (cancelled) return;
        setSchedule(data);
        setChannels(channelsFromSchedule(data));
        setReviewRequired(!!data.reviewRequired);
        setIntervalMinutes(String(data.intervalMinutes ?? 180));
        setDailyLimit(String(data.dailyLimit ?? 5));
        const mix = splitDailyMix(
          data.dailyLimit ?? 5,
          data.personaDailyCount,
          data.productDailyCount,
          Boolean(profile?.description?.trim())
        );
        setPersonaDailyCount(mix.persona);
        setProductDailyCount(mix.product);
        const images = imageCountsFromSchedule(data, mix);
        setPersonaImagePostCount(images.personaImages);
        setProductImagePostCount(images.productImages);
        setProductImagesPerPost(images.perPost);
        setCommentPersona(data.commentPersona ?? '');
        setToneEnabled(data.toneEnabled !== false);
        setReferenceCopyEnabled(!!data.referenceCopyEnabled);
        setReferencePost(data.referencePost ?? '');
        setReferenceResults([]);
        setReferenceSearchError(null);
        setExpandedKeywords([]);
      })
      .catch((err) => {
        if (cancelled) return;
        setScheduleError(err instanceof ApiError ? err.message : '발행 설정을 불러오지 못했습니다. 다시 시도해 주세요.');
      })
      .finally(() => {
        if (!cancelled) setScheduleLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAccountId, profile?.description, scheduleRefresh]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await threadAccountApi.openOAuthLoginForNewAccount();
    } catch (error) {
      setMessage({ tone: 'danger', text: error instanceof Error ? error.message : '계정 연결에 실패했습니다.' });
    } finally {
      setConnecting(false);
    }
  };

  const handleSaveProfile = async (e: FormEvent) => {
    e.preventDefault();
    if (!profile?.id) return;
    setSaving(true);
    setMessage(null);
    try {
      await threadProfileApi.updateProfile(profile.id, { description });
      await fetchAccounts();
      await alert({
        title: '저장 완료',
        message: '계정 페르소나가 저장되었습니다. 소재 기반 글에는 선택적으로 반영됩니다.',
        tone: 'success',
        confirmText: '확인',
      });
    } catch (err) {
      await alert({
        title: '저장 실패',
        message: err instanceof ApiError ? err.message : '저장에 실패했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (selectedAccountId == null) return;
    const ok = await confirm({
      title: '계정 연동 해제',
      message: (
        <>
          <p>
            {displayName || '이 계정'}을(를) 앱에서 연동 해제할까요?
          </p>
          <p style={{ marginTop: 8 }}>
            자동 발행이 꺼지고, 대기 중 예약은 취소되며, 소재 배정이
            해제됩니다. Threads에 올린 글은 삭제되지 않습니다. 같은 계정을
            다시 연동하면 이력을 이어갈 수 있습니다.
          </p>
        </>
      ),
      confirmText: '연동 해제',
      cancelText: '취소',
      tone: 'danger',
    });
    if (!ok) return;

    setDeleting(true);
    setMessage(null);
    try {
      const deletedId = selectedAccountId;
      await threadAccountApi.deleteThreadAccount(deletedId);
      selectAccount(null);
      const nextAccounts = await fetchAccounts();
      const next =
        nextAccounts.find((a) => a.id !== deletedId) ?? nextAccounts[0] ?? null;
      selectAccount(next?.id ?? null);
      await alert({
        title: '연동 해제 완료',
        message: '계정이 앱에서 연동 해제되었습니다.',
        tone: 'success',
        confirmText: '확인',
      });
    } catch (err) {
      await alert({
        title: '연동 해제 실패',
        message:
          err instanceof ApiError
            ? err.message
            : '계정 연동 해제에 실패했습니다. 잠시 후 다시 시도해 주세요.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleAnalyzeTone = async (e: FormEvent) => {
    e.preventDefault();
    if (!profile?.id || !toneEssay.trim()) return;
    setToneSaving(true);
    setToneError(null);
    try {
      const result = await threadProfileApi.analyzeThreadTone(profile.id, {
        essay: toneEssay,
      });
      setTone(result.threadToneProfileResponse ?? null);
      setToneEssay('');
      setToneEnabled(true);
      await fetchAccounts();
      if (selectedAccountId != null) {
        const data = await threadScheduleApi.upsertSchedule(selectedAccountId, {
          toneEnabled: true,
        });
        setSchedule(data);
        setToneEnabled(data.toneEnabled !== false);
      }
      await alert({
        title: '말투 저장 완료',
        message: '말투 사용이 켜졌습니다. 이후 AI 글 생성에 반영됩니다.',
        tone: 'success',
        confirmText: '확인',
      });
    } catch (err) {
      setToneError(
        err instanceof ApiError ? err.message : '말투 설정에 실패했습니다.'
      );
    } finally {
      setToneSaving(false);
    }
  };

  const handleToneEnabledChange = async (next: boolean) => {
    if (selectedAccountId == null || toneToggleSaving) return;
    if (next && !tone) {
      await alert({
        title: '말투 필요',
        message: '먼저 참고 글을 분석해 말투를 저장해 주세요.',
        tone: 'danger',
        confirmText: '확인',
      });
      return;
    }
    const prev = toneEnabled;
    setToneToggleSaving(true);
    setToneEnabled(next);
    try {
      const data = await threadScheduleApi.upsertSchedule(selectedAccountId, {
        toneEnabled: next,
      });
      setSchedule(data);
      setToneEnabled(data.toneEnabled !== false);
    } catch (err) {
      setToneEnabled(prev);
      await alert({
        title: '저장 실패',
        message:
          err instanceof ApiError
            ? err.message
            : '말투 사용 설정을 저장하지 못했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setToneToggleSaving(false);
    }
  };

  const handleSearchReferences = async () => {
    if (selectedAccountId == null || !referenceKeyword.trim() || referenceSearching) return;
    setReferenceSearching(true);
    setReferenceSearchError(null);
    setReferenceSearchErrorTone('danger');
    setExpandedKeywords([]);
    try {
      const data = await threadReferenceSearchApi.searchReferences(
        selectedAccountId,
        referenceKeyword.trim(),
        40,
        referenceStyle
      );
      setReferenceResults(data.posts ?? []);
      setExpandedKeywords(data.expandedKeywords ?? []);
      if (data.warning) {
        setReferenceSearchError(data.warning);
        setReferenceSearchErrorTone('warning');
      } else if (!data.posts?.length) {
        setReferenceSearchError(
          '한국어 인기 글을 찾지 못했습니다. 키워드나 형태 필터를 바꿔 보세요.'
        );
        setReferenceSearchErrorTone('danger');
      }
    } catch (err) {
      setReferenceResults([]);
      setExpandedKeywords([]);
      setReferenceSearchError(
        err instanceof ApiError
          ? err.message
          : '레퍼런스 검색에 실패했습니다.'
      );
      setReferenceSearchErrorTone('danger');
    } finally {
      setReferenceSearching(false);
    }
  };

  const resolveReferenceUrl = (post: ThreadReferencePost): string | null => {
    if (post.permalink?.trim()) {
      return post.permalink.trim();
    }
    if (post.threadMediaId && post.username) {
      const username = post.username.replace(/^@/, '');
      return `https://www.threads.com/@${username}/post/${post.threadMediaId}`;
    }
    return null;
  };

  const formatMetric = (value?: number) => {
    if (value == null) return '-';
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return String(value);
  };

  /** Tavily raw 본문의 '# Thread … views' 메타 줄을 제거하고 글만 보이게 합니다. */
  const displayReferenceText = (text?: string) => {
    if (!text?.trim()) return '(본문 없음 — 원문에서 확인)';
    const cleaned = text
      .replace(/^#\s*Thread[^\n]*/gim, '')
      .replace(/^\s*\d+(?:\.\d+)?[KMB]?\s*views?\s*$/gim, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    return cleaned || text.trim();
  };

  const handleSaveSettings = async (e: FormEvent) => {
    e.preventDefault();
    if (selectedAccountId == null) return;
    if (referenceCopyEnabled && !referencePost.trim()) {
      await alert({
        title: '레퍼런스 필요',
        message:
          '터진 글 따라쓰기를 쓰려면 Threads 원문에서 글을 복사해 아래에 붙여넣어 주세요.',
        tone: 'danger',
        confirmText: '확인',
      });
      return;
    }
    if (channels.personaEnabled && personaDailyCount > 0 && !description.trim()) {
      await alert({
        title: '페르소나 필요',
        message:
          '페르소나 발행 횟수를 쓰려면 위 계정 페르소나를 저장하거나, 페르소나 횟수를 0회로 하세요.',
        tone: 'danger',
        confirmText: '확인',
      });
      return;
    }
    setScheduleSaving(true);
    setMessage(null);
    try {
      const data = await threadScheduleApi.upsertSchedule(selectedAccountId, {
        ...channels,
        reviewRequired,
        intervalMinutes: Number(intervalMinutes) || 180,
        dailyLimit: Number(dailyLimit) || 5,
        personaDailyCount,
        productDailyCount,
        personaImagePostCount,
        productImagePostCount,
        productImagesPerPost,
        commentPersona: commentPersona.trim(),
        toneEnabled,
        referenceCopyEnabled,
        referencePost: referencePost.trim(),
      });
      setSchedule(data);
      setChannels(channelsFromSchedule(data));
        setReviewRequired(!!data.reviewRequired);
      setIntervalMinutes(String(data.intervalMinutes));
      setDailyLimit(String(data.dailyLimit));
      const mix = splitDailyMix(
        data.dailyLimit ?? 5,
        data.personaDailyCount,
        data.productDailyCount,
        Boolean(description.trim())
      );
      setPersonaDailyCount(mix.persona);
      setProductDailyCount(mix.product);
      const images = imageCountsFromSchedule(data, mix);
      setPersonaImagePostCount(images.personaImages);
      setProductImagePostCount(images.productImages);
      setProductImagesPerPost(images.perPost);
      setCommentPersona(data.commentPersona ?? '');
      setToneEnabled(data.toneEnabled !== false);
      setReferenceCopyEnabled(!!data.referenceCopyEnabled);
      setReferencePost(data.referencePost ?? '');

      const parts: string[] = [];
      if (data.toneEnabled !== false) {
        parts.push(
          tone
            ? '저장된 말투를 사용합니다.'
            : '말투 사용이 켜져 있지만 아직 분석된 말투가 없습니다.'
        );
      } else {
        parts.push('말투 사용이 꺼져 있습니다.');
      }
      parts.push(
        data.referenceCopyEnabled
          ? '글 구조는 레퍼런스를 따릅니다.'
          : `하루 페르소나 ${data.personaDailyCount ?? personaDailyCount}회, 소재 ${data.productDailyCount ?? productDailyCount}회를 예약합니다.`
      );
      if (data.enabled) {
        if (data.enqueuedOnSave && data.enqueuedOnSave > 0) {
          parts.push(`오늘 예약 ${data.enqueuedOnSave}건이 보드에 추가되었습니다.`);
        } else if ((data.pendingCount ?? 0) > 0) {
          parts.push(`대기 중 예약 ${data.pendingCount}건이 있습니다.`);
        } else {
          parts.push('자동 발행이 켜졌습니다.');
        }
      } else {
        parts.push('자동 발행은 꺼져 있습니다.');
      }

      await alert({
        title: '설정 저장 완료',
        message: parts.join(' '),
        tone: 'success',
        confirmText: '확인',
      });
    } catch (err) {
      await alert({
        title: '저장 실패',
        message:
          err instanceof ApiError ? err.message : '설정 저장에 실패했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
    const current = tabs.indexOf(event.target as HTMLButtonElement);
    if (current < 0) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    tabs[next]?.focus();
    tabs[next]?.click();
  };

  if (loading && accounts.length === 0) {
    return (
      <Page>
        <Center role="status">
          <Spinner $size={28} aria-hidden="true" />
          계정 정보를 불러오는 중입니다.
        </Center>
      </Page>
    );
  }

  if (!selectedAccount || !selectedAccountId) {
    return (
      <Page>
        <Header>
          <div>
            <Title>계정 설정</Title>
            <Sub>Threads 계정을 연동하면 자동 발행을 시작할 수 있습니다.</Sub>
          </div>
        </Header>
        {(message || error) && (
          <Banner>
            {message && <Alert tone={message.tone}>{message.text}</Alert>}
            {error && <Alert tone="danger">{error}</Alert>}
          </Banner>
        )}
        <Stack>
          {accounts.length > 0 ? (
            <Card>
              <CardTitle>계정 선택</CardTitle>
              <StepDesc>설정을 볼 계정을 골라 주세요.</StepDesc>
              <AccountChipList>
                {accounts.map((account) => {
                  const p = account.threadAccountProfileResponse;
                  const label = p?.threadUserName
                    ? `@${p.threadUserName}`
                    : `계정 #${account.id}`;
                  return (
                    <AccountChip
                      key={account.id}
                      type="button"
                      $active={account.id === selectedAccountId}
                      onClick={() => selectAccount(account.id)}
                    >
                      <Avatar
                        src={p?.threadProfilePictureUrl}
                        name={p?.threadUserName}
                        size={28}
                      />
                      <span>{label}</span>
                    </AccountChip>
                  );
                })}
              </AccountChipList>
              <Actions style={{ marginTop: 16 }}>
                <Button
                  type="button"
                  $variant="secondary"
                  disabled={connecting} onClick={() => void handleConnect()}
                >
                  계정 추가
                </Button>
              </Actions>
            </Card>
          ) : (
            <Card>
              <EmptyState
                title="연동된 계정이 없습니다"
                description="아래 버튼으로 Threads 계정을 연결하세요."
              />
              <Actions style={{ marginTop: 16, justifyContent: 'center' }}>
                <Button
                  type="button"
                  disabled={connecting} onClick={() => void handleConnect()}
                >
                  {connecting ? 'Threads로 이동 중…' : 'Threads 계정 연결'}
                </Button>
              </Actions>
            </Card>
          )}
        </Stack>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <div>
          <Title>계정 설정</Title>
          <Sub>
            계정별 페르소나, 자동 발행 계획과 말투를 한곳에서 관리하세요.
          </Sub>
        </div>
      </Header>

      {(message || error) && (
        <Banner>
          {message && <Alert tone={message.tone}>{message.text}</Alert>}
          {error && <Alert tone="danger">{error}</Alert>}
        </Banner>
      )}

      {(selectedAccount.reconnectRequired || selectedAccount.tokenHealth === 'REFRESH_FAILED' || selectedAccount.tokenHealth === 'EXPIRING') && (
        <Alert tone={selectedAccount.reconnectRequired ? 'danger' : 'warning'}>
          {selectedAccount.reconnectRequired ? '계정 연결을 복구해야 자동화를 계속할 수 있습니다.' : selectedAccount.tokenHealth === 'EXPIRING' ? '계정 연결 만료가 임박했습니다.' : '최근 연결 갱신에 실패했습니다.'}
          <Button type="button" $size="sm" $variant="secondary" disabled={connecting} onClick={() => void handleConnect()}>Threads 다시 연결</Button>
        </Alert>
      )}

      <SwitcherCard>
        <SwitcherHead>
          <div>
            <CardTitle>연결된 계정 <AccountCount>{accounts.length}</AccountCount></CardTitle>
            <StepDesc>아래 설정은 선택한 {displayName} 계정에 적용됩니다.</StepDesc>
          </div>
          <SwitcherActions>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              disabled={connecting} onClick={() => void handleConnect()}
            >
              {connecting ? '연결 중…' : '+ 계정 추가'}
            </Button>
            <Button
              type="button"
              $variant="danger"
              $size="sm"
              disabled={deleting}
              onClick={() => void handleDeleteAccount()}
            >
              {deleting ? '해제 중…' : '연동 해제'}
            </Button>
          </SwitcherActions>
        </SwitcherHead>
        <AccountChipList>
          {accounts.map((account) => {
            const p = account.threadAccountProfileResponse;
            const label = p?.threadUserName
              ? `@${p.threadUserName}`
              : `계정 #${account.id}`;
            return (
              <AccountChip
                key={account.id}
                type="button"
                $active={account.id === selectedAccountId}
                onClick={() => selectAccount(account.id)}
              >
                <Avatar
                  src={p?.threadProfilePictureUrl}
                  name={p?.threadUserName}
                  size={28}
                />
                <ChipText>
                  <ChipName>{label}</ChipName>
                  {account.id === selectedAccountId && (
                    <ChipBadge>선택</ChipBadge>
                  )}
                </ChipText>
              </AccountChip>
            );
          })}
        </AccountChipList>
      </SwitcherCard>

      <ViewTabs role="tablist" aria-label="계정 설정 메뉴" onKeyDown={handleTabKeyDown}>
        <ViewTab
          type="button"
          role="tab"
          id="account-tab-publish"
          aria-controls="account-panel-publish"
          tabIndex={accountTab === 'publish' ? 0 : -1}
          aria-selected={accountTab === 'publish'}
          $active={accountTab === 'publish'}
          onClick={() => setAccountTab('publish')}
        >
          페르소나 · 발행
        </ViewTab>
        <ViewTab
          type="button"
          role="tab"
          id="account-tab-tone"
          aria-controls="account-panel-tone"
          tabIndex={accountTab === 'tone' ? 0 : -1}
          aria-selected={accountTab === 'tone'}
          $active={accountTab === 'tone'}
          onClick={() => setAccountTab('tone')}
        >
          말투 설정
        </ViewTab>
      </ViewTabs>

      <PanelStack>
        <ViewPanel
          role="tabpanel"
          $active={accountTab === 'publish'}
          id="account-panel-publish"
          aria-labelledby="account-tab-publish"
          hidden={accountTab !== 'publish'}
        >
          <Card>
            <StepBadge>1</StepBadge>
            <CardTitle>계정 페르소나</CardTitle>
            <StepDesc>
              어떤 이야기를 누구에게 전할지 한두 문장으로 소개해 주세요. 비워 두면 배정된 소재로만 글을 만듭니다.
            </StepDesc>
            {profile ? (
              <Form onSubmit={handleSaveProfile} aria-busy={saving}>
                <FormFields disabled={saving} aria-label="계정 페르소나 설정">
                  <Field label="계정 소개" htmlFor="description" hint={`${description.length.toLocaleString('ko-KR')} / 5,000자`}>
                    <Textarea
                      id="description"
                      placeholder="예: 알뜰 쇼핑 팁을 편하게 풀어쓰는 계정"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      maxLength={5000}
                    />
                  </Field>
                  <Actions>
                    <Button type="submit" disabled={saving}>
                      {saving ? <><Spinner aria-hidden="true" />저장 중…</> : '페르소나 저장'}
                    </Button>
                    <ActionLink to="/products">소재 등록 →</ActionLink>
                  </Actions>
                </FormFields>
              </Form>
            ) : (
              <EmptyState title="프로필 정보가 없습니다" />
            )}
          </Card>

          <Card>
            <StepBadge>2</StepBadge>
            <CardTitle>자동 발행</CardTitle>
            <StepDesc>
              발행할 유형과 횟수를 선택한 뒤 설정을 저장하세요. 예약된 글은 보드에서 확인하고 수정할 수 있습니다.
            </StepDesc>

            {scheduleLoading ? (
              <Center role="status">
                <Spinner $size={22} aria-hidden="true" />
                발행 설정을 불러오는 중입니다.
              </Center>
            ) : scheduleError ? (
              <LoadError>
                <Alert tone="danger">{scheduleError}</Alert>
                <Button type="button" $variant="secondary" onClick={() => setScheduleRefresh((value) => value + 1)}>발행 설정 다시 불러오기</Button>
              </LoadError>
            ) : (
              <Form onSubmit={handleSaveSettings} aria-busy={scheduleSaving}>
                <FormFields disabled={scheduleSaving} aria-label="자동 발행 설정">
                  <PlanSummary aria-label="선택한 발행 계획">
                    <div><SummaryLabel>자동 발행 유형</SummaryLabel><SummaryValue>{enabled ? `${Object.values(channels).filter(Boolean).length}개 선택` : '모두 꺼짐'}</SummaryValue></div>
                    <div><SummaryLabel>하루 발행 한도</SummaryLabel><SummaryValue>최대 {dailyLimit}회</SummaryValue></div>
                    <div><SummaryLabel>발행 방식</SummaryLabel><SummaryValue>{reviewRequired ? '검토 후 승인' : '자동 발행'}</SummaryValue></div>
                  </PlanSummary>
                  <SectionHeading>글 생성 방식</SectionHeading>
                  <ModeGrid role="group" aria-label="글 생성 방식">
                    <ModeCard
                      type="button"
                      $active={!referenceCopyEnabled}
                      onClick={() => setReferenceCopyEnabled(false)}
                    >
                      <ModeTitle>알아서 쓰기</ModeTitle>
                      <ModeHelp>
                        {toneEnabled && tone
                          ? '저장된 말투로 새 글을 만듭니다. 아래 횟수만큼 페르소나/소재를 섞어 예약합니다.'
                          : '말투 없이 새 글을 만듭니다. 아래 횟수만큼 페르소나/소재를 섞어 예약합니다.'}
                      </ModeHelp>
                    </ModeCard>
                    <ModeCard
                      type="button"
                      $active={referenceCopyEnabled}
                      onClick={() => setReferenceCopyEnabled(true)}
                    >
                      <ModeTitle>터진 글 따라쓰기</ModeTitle>
                      <ModeHelp>
                        잘 된 글의 구조를 그대로 가져와 소재만 바꿉니다.
                      </ModeHelp>
                    </ModeCard>
                  </ModeGrid>

                  {referenceCopyEnabled && (
                    <ReferencePanel>
                      <SearchRow>
                        <Field label="레퍼런스 검색" htmlFor="referenceKeyword">
                        <Input
                          id="referenceKeyword"
                          placeholder="찾고 싶은 소재 (예: 폐점 세일)"
                          value={referenceKeyword}
                          disabled={referenceSearching}
                          onChange={(e) => setReferenceKeyword(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void handleSearchReferences();
                            }
                          }}
                        />
                        </Field>
                        <Button
                          type="button"
                          disabled={
                            referenceSearching || !referenceKeyword.trim()
                          }
                          onClick={() => void handleSearchReferences()}
                        >
                          {referenceSearching ? <><Spinner aria-hidden="true" />검색 중…</> : '검색'}
                        </Button>
                      </SearchRow>
                      <Hint>키워드로 글을 찾은 뒤 원문에서 참고할 내용을 복사하세요.</Hint>

                      <StyleChips role="group" aria-label="글 스타일">
                        {REFERENCE_POST_STYLES.map((option) => (
                          <StyleChip
                            key={option.value}
                            type="button"
                            $active={referenceStyle === option.value}
                            title={option.help}
                            onClick={() => setReferenceStyle(option.value)}
                          >
                            {option.label}
                          </StyleChip>
                        ))}
                      </StyleChips>

                      {expandedKeywords.length > 0 && (
                        <ExpandedKeywords>
                          {expandedKeywords.map((kw) => (
                            <ExpandedKeyword key={kw}>{kw}</ExpandedKeyword>
                          ))}
                        </ExpandedKeywords>
                      )}

                      {referenceSearchError && (
                        <Alert tone={referenceSearchErrorTone}>
                          {referenceSearchError}
                        </Alert>
                      )}

                      {referenceResults.length > 0 && (
                        <ResultList>
                          {referenceResults.map((post, index) => {
                            const referenceUrl = resolveReferenceUrl(post);
                            const displayText = displayReferenceText(post.text);

                            return (
                              <ResultCard
                                key={`${post.permalink ?? post.threadMediaId ?? index}`}
                                as={referenceUrl ? 'a' : 'div'}
                                href={referenceUrl ?? undefined}
                                target={referenceUrl ? '_blank' : undefined}
                                rel={
                                  referenceUrl ? 'noopener noreferrer' : undefined
                                }
                              >
                                <ResultSelect>
                                  <ResultHeader>
                                    <ResultIdentity>
                                      <ResultUser>
                                        {post.username
                                          ? `@${post.username}`
                                          : 'Threads'}
                                      </ResultUser>
                                      {(post.styleLabel || post.style) && (
                                        <ResultStyle>
                                          {post.styleLabel ?? post.style}
                                        </ResultStyle>
                                      )}
                                    </ResultIdentity>
                                    <ResultStats>
                                      {post.views != null && (
                                        <span>
                                          조회 {formatMetric(post.views)}
                                        </span>
                                      )}
                                    </ResultStats>
                                  </ResultHeader>
                                  <ResultText>{displayText}</ResultText>
                                  <ResultHint>
                                    {referenceUrl
                                      ? '탭하면 Threads에서 원문 확인 → 필요한 부분만 아래에 붙여넣기'
                                      : '원문 링크 없음 · 미리보기만 참고하고 아래에 직접 붙여넣기'}
                                  </ResultHint>
                                </ResultSelect>
                              </ResultCard>
                            );
                          })}
                        </ResultList>
                      )}

                      <PasteBlock>
                        <PresetLabel as="label" htmlFor="referencePost">레퍼런스 붙여넣기</PresetLabel>
                        <Hint>
                          원문에서 글 구조만 복사해 붙여넣으세요. 조회수·해시태그
                          등 불필요한 정보는 빼 주세요.
                        </Hint>
                        <Textarea
                          id="referencePost"
                          placeholder="Threads 원문에서 복사한 글을 여기에 붙여넣기"
                          value={referencePost}
                          onChange={(e) => setReferencePost(e.target.value)}
                          rows={referencePost.trim() ? 8 : 5}
                        />
                        {referencePost.trim() && (
                          <Actions>
                            <Button
                              type="button"
                              $variant="ghost"
                              $size="sm"
                              onClick={() => setReferencePost('')}
                            >
                              지우기
                            </Button>
                          </Actions>
                        )}
                      </PasteBlock>
                    </ReferencePanel>
                  )}

                  <Divider />

                  <SectionHeading>자동 발행할 유형</SectionHeading>
                  <ToggleGrid>
                    {autoPublishChannels.map(({ key, label }) => <ToggleRow key={key}>
                      <ToggleLabel>{label} 자동 발행</ToggleLabel>
                      <Switch checked={channels[key]} aria-label={label + ' 자동 발행'}
                        onChange={(next) => setChannels((previous) => ({ ...previous, [key]: next }))} />
                    </ToggleRow>)}
                  </ToggleGrid>

                  <Field
                    label="댓글 페르소나"
                    htmlFor="commentPersona"
                    hint="선택"
                    help="발행 후 자동으로 다는 댓글의 말투·캐릭터입니다. 자동 댓글 사용 여부는 소재에서 설정합니다."
                  >
                    <Textarea
                      id="commentPersona"
                      placeholder="예: 본문보다 캐주얼하게, 친한 동생처럼. 링크는 둘째 댓글에만 자연스럽게."
                      value={commentPersona}
                      onChange={(e) => setCommentPersona(e.target.value)}
                      rows={4}
                      maxLength={4000}
                    />
                  </Field>

                  {enabled && (
                    <Hint>
                      {description.trim()
                        ? `켜진 유형만 자동 생성하며 하루 ${dailyLimit}회 한도를 공유합니다. 페르소나 ${personaDailyCount}회(사진 ${personaImagePostCount}회), 소재 ${productDailyCount}회(사진 ${productImagePostCount}회, 글당 ${productImagesPerPost}장)를 예약합니다. 소재 횟수는 직접 소재와 유튜브가 공유하며, 노션은 가져온 예약만 발행합니다.`
                        : '켜진 유형의 배정 소재·유튜브를 자동 생성하고, 노션은 가져온 예약을 발행합니다. 하루 발행 한도는 모든 유형이 공유합니다.'}
                    </Hint>
                  )}

                  <PresetBlock role="group" aria-label="발행 전 검토">
                    <PresetLabel>발행 전 검토</PresetLabel>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Switch checked={reviewRequired} onChange={setReviewRequired} aria-label="승인 후 발행" />
                      승인한 초안만 자동 발행
                    </label>
                    <Hint>켜면 새 초안이 승인 대기로 준비됩니다. 보드에서 본문과 이미지를 검토한 뒤 승인하세요.</Hint>
                  </PresetBlock>

                  <SectionHeading>발행 횟수와 이미지</SectionHeading>
                  <ScheduleGrid>
                  <PresetBlock role="group" aria-label="발행 간격">
                    <PresetLabel>발행 간격</PresetLabel>
                    <PresetRow>
                      {[
                        { value: '180', label: '3시간' },
                        { value: '240', label: '4시간' },
                        { value: '360', label: '6시간' },
                      ].map((opt) => (
                        <StyleChip
                          key={opt.value}
                          type="button"
                          $active={intervalMinutes === opt.value}
                          onClick={() => setIntervalMinutes(opt.value)}
                        >
                          {opt.label}
                        </StyleChip>
                      ))}
                    </PresetRow>
                  </PresetBlock>

                  <PresetBlock role="group" aria-label="하루 최대">
                    <PresetLabel>하루 최대</PresetLabel>
                    <PresetRow>
                      {[
                        { value: '1', label: '1회' },
                        { value: '3', label: '3회' },
                        { value: '5', label: '5회' },
                      ].map((opt) => (
                        <StyleChip
                          key={opt.value}
                          type="button"
                          $active={dailyLimit === opt.value}
                          onClick={() => {
                            const next = Number(opt.value);
                            setDailyLimit(opt.value);
                            const p = Math.min(personaDailyCount, next);
                            setPersonaDailyCount(p);
                            setProductDailyCount(next - p);
                            setPersonaImagePostCount((c) => Math.min(c, p));
                            setProductImagePostCount((c) => Math.min(c, next - p));
                          }}
                        >
                          {opt.label}
                        </StyleChip>
                      ))}
                    </PresetRow>
                  </PresetBlock>

                  <PresetBlock role="group" aria-label="페르소나 발행">
                    <PresetLabel>페르소나 발행</PresetLabel>
                    <PresetRow>
                      {MIX_COUNTS.map((n) => (
                        <StyleChip
                          key={`persona-${n}`}
                          type="button"
                          disabled={n > Number(dailyLimit)}
                          $active={personaDailyCount === n}
                          onClick={() => {
                            const limit = Number(dailyLimit) || 5;
                            const p = Math.min(n, limit);
                            setPersonaDailyCount(p);
                            setProductDailyCount(limit - p);
                            setPersonaImagePostCount((c) => Math.min(c, p));
                            setProductImagePostCount((c) => Math.min(c, limit - p));
                          }}
                        >
                          {n}회
                        </StyleChip>
                      ))}
                    </PresetRow>
                  </PresetBlock>

                  <PresetBlock role="group" aria-label="소재 발행">
                    <PresetLabel>소재 발행</PresetLabel>
                    <PresetRow>
                      {MIX_COUNTS.map((n) => (
                        <StyleChip
                          key={`product-${n}`}
                          type="button"
                          disabled={n > Number(dailyLimit)}
                          $active={productDailyCount === n}
                          onClick={() => {
                            const limit = Number(dailyLimit) || 5;
                            const r = Math.min(n, limit);
                            setProductDailyCount(r);
                            setPersonaDailyCount(limit - r);
                            setProductImagePostCount((c) => Math.min(c, r));
                            setPersonaImagePostCount((c) => Math.min(c, limit - r));
                          }}
                        >
                          {n}회
                        </StyleChip>
                      ))}
                    </PresetRow>
                    <Hint>
                      합계는 하루 최대와 같습니다. 페르소나 0회면 소재만, 소재
                      0회면 페르소나만 예약합니다.
                    </Hint>
                  </PresetBlock>

                  <PresetBlock role="group" aria-label="페르소나 사진">
                    <PresetLabel>페르소나 사진</PresetLabel>
                    <PresetRow>
                      {MIX_COUNTS.map((n) => (
                        <StyleChip
                          key={`persona-image-${n}`}
                          type="button"
                          disabled={n > personaDailyCount}
                          $active={personaImagePostCount === n}
                          onClick={() =>
                            setPersonaImagePostCount(Math.min(n, personaDailyCount))
                          }
                        >
                          {n}회
                        </StyleChip>
                      ))}
                    </PresetRow>
                    <Hint>
                      하루 페르소나 글 중 AI 사진을 넣을 횟수입니다. 글당 1장입니다.
                    </Hint>
                  </PresetBlock>

                  <PresetBlock role="group" aria-label="소재 사진">
                    <PresetLabel>소재 사진</PresetLabel>
                    <PresetRow>
                      {MIX_COUNTS.map((n) => (
                        <StyleChip
                          key={`product-image-${n}`}
                          type="button"
                          disabled={n > productDailyCount}
                          $active={productImagePostCount === n}
                          onClick={() =>
                            setProductImagePostCount(Math.min(n, productDailyCount))
                          }
                        >
                          {n}회
                        </StyleChip>
                      ))}
                    </PresetRow>
                    <Hint>
                      하루 소재 글 중 등록한 사진을 넣을 횟수입니다. 유튜브 소재는
                      사진 없이 나갑니다.
                    </Hint>
                  </PresetBlock>

                  <PresetBlock role="group" aria-label="소재 글당 사진">
                    <PresetLabel>소재 글당 사진</PresetLabel>
                    <PresetRow>
                      {IMAGE_PER_POST.map((n) => (
                        <StyleChip
                          key={`per-post-${n}`}
                          type="button"
                          $active={productImagesPerPost === n}
                          onClick={() => setProductImagesPerPost(n)}
                        >
                          {n}장
                        </StyleChip>
                      ))}
                    </PresetRow>
                    <Hint>
                      한 글에 2장 이상이면 캐러셀로 나갑니다. 남은 사진이 부족하면
                      있는 만큼만 붙입니다.
                    </Hint>
                  </PresetBlock>

                  </ScheduleGrid>

                  {schedule?.nextRunAt && enabled && (
                    <Hint>
                      다음 예약: {formatWallClockKo(schedule.nextRunAt)}
                    </Hint>
                  )}

                  <SaveBar>
                    <Hint>변경한 발행 계획은 저장 후 적용됩니다.</Hint>
                    <Actions>
                      <ActionLink to="/board">보드 보기 →</ActionLink>
                      <Button type="submit" disabled={scheduleSaving}>
                        {scheduleSaving ? <><Spinner aria-hidden="true" />저장 중…</> : '발행 설정 저장'}
                      </Button>
                    </Actions>
                  </SaveBar>
                </FormFields>
              </Form>
            )}
          </Card>
        </ViewPanel>

        <ViewPanel
          role="tabpanel"
          $active={accountTab === 'tone'}
          id="account-panel-tone"
          aria-labelledby="account-tab-tone"
          hidden={accountTab !== 'tone'}
        >
          <Card>
            <StepBadge>AI</StepBadge>
            <CardTitle>말투 설정</CardTitle>
            <StepDesc>
              참고 글을 분석해 이 계정만의 말투를 저장합니다. 토글을 켜야 AI
              글 생성에 반영됩니다.
            </StepDesc>

            <ToggleRow>
              <div>
                <ToggleLabel>설정된 말투 사용</ToggleLabel>
                <ToggleHelp>
                  {!tone
                    ? '먼저 아래에서 참고 글을 분석해 말투를 저장하세요.'
                    : toneEnabled
                      ? '켜면 저장된 말투로 글을 씁니다. 끄면 말투 없이 생성합니다.'
                      : '현재 새 글에 말투를 적용하지 않습니다. 저장된 말투는 유지됩니다.'}
                </ToggleHelp>
              </div>
              <Switch
                checked={!!tone && toneEnabled}
                disabled={!tone || toneToggleSaving || scheduleLoading || !!scheduleError || toneSaving}
                aria-label="설정된 말투 사용"
                onChange={(next) => void handleToneEnabledChange(next)}
              />
            </ToggleRow>

            {toneError && <Alert tone="danger">{toneError}</Alert>}

            <>
                {profile?.id && (
                  <TonePanel>
                    <HintRow>
                      {TONE_EXAMPLE_HINTS.map((hint) => (
                        <HintChip
                          key={hint}
                          type="button"
                          disabled={toneSaving}
                          onClick={() =>
                            setToneEssay((prev) =>
                              prev ? `${prev}\n\n${hint}` : hint
                            )
                          }
                        >
                          {hint}
                        </HintChip>
                      ))}
                    </HintRow>
                    <Form onSubmit={handleAnalyzeTone} aria-busy={toneSaving}>
                      <FormFields disabled={toneSaving || toneToggleSaving} aria-label="말투 분석">
                        <Field label="참고 글" htmlFor="toneEssay" help="직접 작성한 글을 여러 개 넣으면 자주 쓰는 표현과 문장 스타일을 더 잘 파악할 수 있습니다.">
                          <Textarea
                            id="toneEssay"
                            style={{ minHeight: 140 }}
                            placeholder="Threads에 올렸던 글, 메모 등을 붙여넣어 주세요."
                            value={toneEssay}
                            onChange={(e) => setToneEssay(e.target.value)}
                            required
                          />
                        </Field>
                        <Actions>
                          <CharCount>
                            {toneEssay.length.toLocaleString('ko-KR')}자
                          </CharCount>
                          <Button
                            type="submit"
                            disabled={toneSaving || !toneEssay.trim()}
                          >
                            {toneSaving ? <><Spinner aria-hidden="true" />말투 분석 중…</> : '분석하고 저장'}
                          </Button>
                        </Actions>
                      </FormFields>
                    </Form>
                  </TonePanel>
                )}

                {tone ? (
                  <ToneSaved>
                    <PresetLabel>저장된 말투</PresetLabel>
                    <ToneProfileView tone={tone} />
                  </ToneSaved>
                ) : (
                  <Hint>
                    아직 저장된 말투가 없습니다. 참고 글을 분석해 주세요.
                  </Hint>
                )}
            </>
          </Card>
        </ViewPanel>
      </PanelStack>
    </Page>
  );
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;

  button:focus-visible, a:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 3px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: clamp(26px, 3vw, 32px);
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: -0.02em;
`;

const Sub = styled.p`
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SwitcherCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: clamp(18px, 3vw, 24px);
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const SwitcherHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SwitcherActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const AccountChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AccountChip = styled.button.attrs<{ $active: boolean }>(({ $active }) => ({ 'aria-pressed': $active }))<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-height: 44px;
  padding: 6px 12px 6px 6px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.primary : theme.colors.border};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.surfaceMuted};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const ChipText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const ChipName = styled.span`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
`;

const ChipBadge = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const AccountCount = styled.span`
  display: inline-grid;
  place-items: center;
  min-width: 24px;
  height: 24px;
  margin-left: 6px;
  padding: 0 7px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  vertical-align: middle;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ViewTabs = styled.div`
  display: inline-flex;
  align-self: stretch;
  gap: 4px;
  padding: 4px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  width: 100%;
  max-width: 420px;
`;

const ViewTab = styled.button<{ $active: boolean }>`
  flex: 1;
  min-height: 44px;
  padding: 10px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.textSecondary};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.surface : 'transparent'};
  box-shadow: ${({ $active, theme }) => ($active ? theme.shadow.sm : 'none')};
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PanelStack = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
`;

const ViewPanel = styled.div<{ $active: boolean }>`
  display: ${({ $active }) => $active ? 'flex' : 'none'};
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;
`;

const Card = styled.section`
  position: relative;
  padding: clamp(18px, 3vw, 28px);
  min-width: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
`;

const StepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-bottom: 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 700;
`;

const CardTitle = styled.h2`
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 6px;
`;

const StepDesc = styled.p`
  margin: 0 0 22px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.65;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormFields = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

const LoadError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
`;

const PlanSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 16px;
    > div { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
  }
`;

const SummaryLabel = styled.span`
  display: block;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
`;

const SummaryValue = styled.strong`
  font-size: 18px;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
`;

const SectionHeading = styled.h3`
  margin: 4px 0 -8px;
  font-size: 16px;
  font-weight: 700;
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  > div {
    padding: 18px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.radius.md};
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const SaveBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding-top: 22px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: 560px) {
    > div { width: 100%; justify-content: space-between; }
  }
`;

const ActionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 600;

  &:hover { background: ${({ theme }) => theme.colors.primarySoft}; }
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ModeCard = styled.button.attrs<{ $active?: boolean }>(({ $active }) => ({ 'aria-pressed': !!$active }))<{ $active?: boolean }>`
  text-align: left;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.surfaceMuted};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
`;

const ModeTitle = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ModeHelp = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ReferencePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: 8px 0;
`;

const ToggleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: stretch;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  min-width: 0;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

const ToggleHelp = styled.p`
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const TonePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 22px;
`;

const ToneSaved = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const HintRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const HintChip = styled.button`
  min-height: 44px;
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 13px;
  text-align: left;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  border: 1px solid transparent;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CharCount = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Hint = styled.p`
  font-size: 13px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PresetBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PresetLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;

  > label {
    flex: 1;
    min-width: 0;
  }

  input {
    flex: 1;
    min-width: 0;
  }
`;

const StyleChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const StyleChip = styled.button.attrs<{ $active?: boolean }>(({ $active }) => ({ 'aria-pressed': !!$active }))<{ $active?: boolean }>`
  min-width: 44px;
  min-height: 44px;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ExpandedKeywords = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ExpandedKeyword = styled.span`
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 12px;
`;

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(55vh, 480px);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
`;

const ResultCard = styled.div`
  flex: 0 0 auto;
  width: 100%;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  touch-action: pan-y;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const ResultSelect = styled.div`
  display: block;
  width: 100%;
  padding: 14px 16px;
  text-align: left;
`;

const PasteBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ResultHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;

const ResultIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

const ResultUser = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ResultStyle = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
`;

const ResultStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ResultText = styled.div`
  font-size: 14px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-height: 10.56em;
  overflow: hidden;
`;

const ResultHint = styled.div`
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;
  padding: 32px;
  color: ${({ theme }) => theme.colors.primary};
`;
